"use client";

import Image from "next/image";
import React from "react";
import Link from "next/link";

interface NewsCardHeroProps {
  image: string;
  date: string;
  category: string;
  title: string;
  excerpt: string;
  author?: string;
  quoteMode?: boolean;
  slug?: string;
}

const NewsCardHero: React.FC<NewsCardHeroProps> = ({
  image,
  date,
  category,
  title,
  excerpt,
  author,
  quoteMode = false,
  slug = "news-post",
}) => {
  return (
    <Link href={`/news-pr/${slug}`} className="block group">
      <div className="w-full overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl">
        <div className="relative w-full aspect-video overflow-hidden">
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-110"
            quality={100}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/api/placeholder/800/600';
            }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
        </div>

        <div className="py-9 px-6">
          {quoteMode ? (
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-[0_8px_24px_rgba(0,0,0,0.08)] border border-[#ececec] transition-all duration-300 group-hover:shadow-[0_12px_40px_rgba(0,0,0,0.15)] group-hover:bg-gray-50">
              <div className="flex items-start gap-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-1 text-[#E0301E] transition-all duration-300 group-hover:scale-110"><path d="M7.17 6C5.97 6 5 6.97 5 8.17v3.66C5 13.03 5.97 14 7.17 14H9v4H5.83C4.27 18 3 16.73 3 15.17V8.83C3 7.27 4.27 6 5.83 6H7.17ZM18.17 6C16.97 6 16 6.97 16 8.17v3.66C16 13.03 16.97 14 18.17 14H20v4h-3.17C15.27 18 14 16.73 14 15.17V8.83C14 7.27 15.27 6 16.83 6h1.34Z" fill="currentColor"/></svg>
                <p className="text-[#1b1b1b] italic leading-relaxed transition-colors duration-300 group-hover:text-[#333]">
                  {excerpt}
                </p>
              </div>
              {author && (
                <div className="mt-4 text-[#E0301E] font-semibold transition-all duration-300 group-hover:scale-105">{author}</div>
              )}
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-[#1b1b1b] transition-colors duration-300 group-hover:text-[#272D57]">{date}</span>
                <span className="text-[#5060A1] font-bold transition-all duration-300 group-hover:text-[#E0301E] group-hover:scale-105">{category}</span>
              </div>
              <h3 className="text-[#272D57] text-2xl font-semibold leading-tight my-4 transition-all duration-300 group-hover:text-[#E0301E]">
                {title}
              </h3>
              <p className="text-[#1b1b1b] leading-snug transition-colors duration-300 group-hover:text-[#333]">{excerpt}</p>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default NewsCardHero;